import React, { useEffect, useMemo } from "react";
import { Dropdown, Header, Icon, Loader, Table, Pagination, Button } from "semantic-ui-react";

import "./styles.scss";
import {
  BranchResult,
  CampaignResult,
  CoachResult,
  ContentPageResult,
  EntityType,
  isEngagementMetric,
  isFollowerMetric,
  isVideoPageMetric,
  LeaderboardMetric,
  LeaderboardResult,
  StatsServices,
  PlayerResult,
  PostResult,
  PromptResult,
  RegionResult,
  SocialIdentityResult,
  VideoScriptResult,
} from "@/services/bedrock";
import { SocialNetworkIcon } from "@/components/SocialNetworkIcon";
import { capitalize } from "lodash";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { exportLeaderboardToCsv, printDateTime, safeFormatNumber, URLS, useSocialCoachSession } from "@/utils";

interface LeaderboardTableProps {
  adminView?: boolean;
  title: string;
  entityType: EntityType;
  perPage?: number;
  paginated?: boolean;
  defaultMetric?: LeaderboardMetric;
  defaultStartTimeKey?: string;
  playerIdFilter?: string | number;

  onViewAll?: (d: string, m: LeaderboardMetric) => void;
  onPaginationUpdate?: (p: Pagination) => void;
}

interface TimeOption {
  key: string;
  label: string;
  value: Date | undefined;
}

interface MetricOption {
  text: string;
  value: LeaderboardMetric;
}

type Props = LeaderboardTableProps;

const LeaderboardTableFC: React.FC<Props> = ({
  adminView,
  title,
  entityType,
  paginated,
  perPage,
  defaultMetric,
  defaultStartTimeKey,
  onViewAll,
  playerIdFilter,
}) => {
  const { coachId } = useSocialCoachSession();
  const [page, setPage] = React.useState<number>(1);

  const getStartOfPastDate = (daysAgo: number): Date => {
    const date = new Date();
    date.setHours(0, 0, 0, 0);
    date.setDate(date.getDate() - daysAgo);
    return date;
  };

  const allMetricOptions: MetricOption[] = [
    { text: "Posts", value: "post_count" },
    { text: "Followers", value: "follower_count" },
    { text: "Follower Growth", value: "follower_change" },
    { text: "Follower Growth %", value: "follower_change_percent" },
    { text: "Impressions", value: "impression_count" },
    { text: "Reactions", value: "total_reactions" },
    { text: "Shares", value: "share_count" },
    { text: "Comments", value: "comment_count" },
    { text: "Views", value: "view_count" },
    { text: "Clicks", value: "click_count" },
  ];

  const metricOptions = useMemo(() => {
    switch (entityType) {
      case "post":
        return allMetricOptions.filter(m => isEngagementMetric(m.value));
      case "social_identity":
        return allMetricOptions.filter(m => isFollowerMetric(m.value));
      case "prompt":
      case "campaign":
      case "video_script":
        return allMetricOptions.filter(m => !isFollowerMetric(m.value));
      case "content_page":
        return allMetricOptions.filter(m => isVideoPageMetric(m.value));
      default:
        return allMetricOptions;
    }
  }, [entityType]);

  const findMetricOption = (v: any): MetricOption | undefined => {
    if (v == undefined) return undefined;
    return metricOptions.find(m => m.value === v);
  };

  const [metric, setMetric] = React.useState<MetricOption>(findMetricOption(defaultMetric) || metricOptions[0]!!);
  const [exporting, setExporting] = React.useState<boolean>(false);
  const effectivePerPage = useMemo(() => perPage || 5, [perPage]);

  const timeOptions: TimeOption[] = useMemo(() => {
    if (metric?.value === "follower_count") return [{ key: "all", label: "All Time", value: undefined }];

    return [
      { key: "7d", label: "Last 7 days", value: getStartOfPastDate(7) },
      { key: "30d", label: "Last 30 days", value: getStartOfPastDate(30) },
      { key: "90d", label: "Last 90 days", value: getStartOfPastDate(90) },
      { key: "365d", label: "Last 365 days", value: getStartOfPastDate(365) },
      { key: "all", label: "All Time", value: undefined },
    ];
  }, [metric?.value]);
  const findTimeOption = (k: any): TimeOption | undefined => {
    if (k == undefined) return undefined;
    return timeOptions.find(m => m.key === k);
  };
  const [startTime, setStartTime] = React.useState<TimeOption>(
    findTimeOption(defaultStartTimeKey) || timeOptions[1] || timeOptions[0]!!
  );

  useEffect(() => {
    const newMetric = findMetricOption(defaultMetric);
    if (newMetric) setMetric(newMetric);
  }, [defaultMetric]);

  useEffect(() => {
    const newTime = findTimeOption(defaultStartTimeKey);
    if (newTime) setStartTime(newTime);
  }, [defaultStartTimeKey]);

  const {
    data: boardStats,
    isLoading,
    isFetching,
  } = useQuery({
    queryKey: [
      "leaderboard",
      coachId,
      playerIdFilter,
      entityType,
      metric.value,
      startTime?.key,
      page,
      effectivePerPage,
    ],
    queryFn: async () => {
      return StatsServices.getLeaderboard({
        coachId: coachId,
        playerIds: playerIdFilter ? [playerIdFilter] : undefined,
        page: page,
        perPage: effectivePerPage,
        metric: metric.value,
        entityType: entityType,
        startTime: startTime?.value,
      });
    },
    enabled: adminView || !!coachId,
    placeholderData: keepPreviousData,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });

  const handleTimeChange = (newLabel: string) => {
    const time = timeOptions.find(t => t.label === newLabel);
    if (time) {
      setStartTime(time);
      setPage(1);
    }
  };

  const handleMetricChange = (newMetricValue: LeaderboardMetric) => {
    const newMetric = metricOptions.find(m => m.value === newMetricValue);
    if (newMetric) {
      setMetric(newMetric);
      setPage(1);
    }
  };

  const totalPages = useMemo(() => boardStats?.pagination?.totalPages || 1, [boardStats?.pagination?.totalPages]);

  const exportStats = async () => {
    setExporting(true);

    const allResults: LeaderboardResult[] = [];
    const exportingPerPage = 100;
    let hasMorePages = true;
    let exportPage = 1;
    while (hasMorePages) {
      const results = await StatsServices.getLeaderboard({
        coachId: coachId,
        playerIds: playerIdFilter ? [playerIdFilter] : undefined,
        page: exportPage,
        perPage: exportingPerPage,
        metric: metric.value,
        entityType: entityType,
        startTime: startTime?.value,
      });
      exportPage++;
      hasMorePages = exportPage <= results.pagination.totalPages;
      allResults.push(...results.results);
    }

    const title = `SocialCoach ${capitalize(entityType)} Leaderboard - ${metric.text} - ${startTime?.label || ""}`;
    exportLeaderboardToCsv(title, metric.value, allResults);
    setExporting(false);
  };

  const trophy = (rank: number) => {
    if (rank <= 3) {
      const color = rank == 1 ? "#FFD700" : rank == 2 ? "#CCCCCC" : "#E0B68A";
      return (
        <Icon name="trophy" style={{ color }} size={"large"}>
          <span className={"trophyRank"}>{rank}</span>
        </Icon>
      );
    } else return <span className={"nonTrophyRank"}>{rank}</span>;
  };

  const statHeaders = useMemo(() => {
    switch (entityType) {
      case "post":
        return (
          <>
            <Table.HeaderCell>Post</Table.HeaderCell>
            <Table.HeaderCell>Info</Table.HeaderCell>
            <Table.HeaderCell>Caption</Table.HeaderCell>
          </>
        );
      case "coach":
        return (
          <>
            <Table.HeaderCell>Coach</Table.HeaderCell>
            <Table.HeaderCell>Owner</Table.HeaderCell>
          </>
        );
      case "social_identity":
        return (
          <>
            <Table.HeaderCell>Account</Table.HeaderCell>
            <Table.HeaderCell>Player</Table.HeaderCell>
          </>
        );
      case "content_page":
        return (
          <>
            <Table.HeaderCell>Page</Table.HeaderCell>
            <Table.HeaderCell>Player</Table.HeaderCell>
          </>
        );
      default:
        return (
          <>
            <Table.HeaderCell>{capitalize(entityType.replaceAll("_", " "))}</Table.HeaderCell>
          </>
        );
    }
  }, [entityType]);

  const statPrefix = (stat: LeaderboardResult) => {
    switch (metric.value) {
      case "follower_change_percent":
      case "follower_change":
        return stat.score > 0 ? "+" : "";
      default:
        return "";
    }
  };

  const statSuffix = (stat: LeaderboardResult) => {
    switch (metric.value) {
      case "follower_change_percent":
        return "%";
      default:
        return "";
    }
  };

  const renderCells = (stat: LeaderboardResult) => {
    switch (entityType) {
      case "post":
        let postStat = stat as PostResult;
        return (
          <>
            <Table.Cell>
              <a href={postStat.externalPostUrl || ""} target="_blank">
                <SocialNetworkIcon socialNetworkId={postStat.socialNetworkId} size="tiny" />
              </a>
            </Table.Cell>
            <Table.Cell className={"statInfo"}>
              <div className={"postPlayerLabel"}>
                <div className={"subtitle-primary nowrap"}>
                  {postStat.firstName} {postStat.lastName}
                </div>
                <div className={"subtitle nowrap"}>Posted {printDateTime(postStat.postedTimestamp)}</div>
              </div>
            </Table.Cell>
            <Table.Cell>
              <div className={"captionText"}>
                {postStat.postedCaption} {postStat.postedCaption} {postStat.postedCaption}
              </div>
            </Table.Cell>
          </>
        );

      case "social_identity":
        const socialIdentityStat = stat as SocialIdentityResult;
        return (
          <>
            <Table.Cell>
              <a
                href={socialIdentityStat.publicUrl || ""}
                target="_blank"
                className={"socialIdentityLink subtitle-primary"}
              >
                <SocialNetworkIcon socialNetworkId={socialIdentityStat.socialNetworkId} size="tiny" />
                <span className={"subtitle-primary"}>
                  {socialIdentityStat.username ? `@${socialIdentityStat.username}` : socialIdentityStat.displayName}
                </span>
              </a>
            </Table.Cell>
            <Table.Cell className={"statInfo subtitle-primary"}>
              {socialIdentityStat.firstName} {socialIdentityStat.lastName}
            </Table.Cell>
          </>
        );

      case "prompt":
        let promptStat = stat as PromptResult;
        return (
          <Table.Cell className={"statInfo"}>
            <a href={URLS.coach.prompt.replace(":promptId", `${stat.id}`)} target="_blank">
              {promptStat.title}
            </a>
          </Table.Cell>
        );

      case "campaign":
        let campaignStat = stat as CampaignResult;
        return (
          <Table.Cell className={"statInfo"}>
            <a href={URLS.coach.editCampaigns.replace(":campaignId", stat.id.toString())} target="_blank">
              {campaignStat.name}
            </a>
          </Table.Cell>
        );

      case "video_script":
        let videoScriptStat = stat as VideoScriptResult;
        return (
          <Table.Cell className={"statInfo"}>
            <a href={URLS.videoScript.replace(":id", videoScriptStat.id.toString())} target="_blank">
              {videoScriptStat.title}
            </a>
          </Table.Cell>
        );

      case "content_page":
        let contentPageStat = stat as ContentPageResult;
        return (
          <>
            <Table.Cell>
              <div className={"postPlayerLabel"}>
                <div className={"subtitle-primary nowrap"}>
                  <a href={contentPageStat.url} target="_blank">
                    {contentPageStat.title || "Untitled"}
                  </a>
                </div>
                <div className={"subtitle nowrap"}>Created {printDateTime(contentPageStat.createdDate)}</div>
              </div>
            </Table.Cell>
            <Table.Cell className={"subtitle-primary"}>
              {contentPageStat.firstName} {contentPageStat.lastName}
            </Table.Cell>
          </>
        );

      case "player":
        let playerStat = stat as PlayerResult;
        return (
          <Table.Cell className={"statInfo"}>
            <a href={`/coach-players/${stat.id}`} target="_blank">
              {playerStat.firstName} {playerStat.lastName}
            </a>
          </Table.Cell>
        );

      case "coach":
        let coachStat = stat as CoachResult;
        return (
          <>
            <Table.Cell>
              <span className={"subtitle"}>{coachStat.companyName}</span>
            </Table.Cell>
            <Table.Cell className={"subtitle-primary"}>
              <div>
                {coachStat.firstName} {coachStat.lastName}
              </div>
              <div>{coachStat.email}</div>
            </Table.Cell>
          </>
        );

      case "region":
        let regionStat = stat as RegionResult;
        return <Table.Cell className={"statInfo"}>{regionStat.name}</Table.Cell>;

      case "branch":
        let branchStat = stat as BranchResult;
        return <Table.Cell className={"statInfo"}>{branchStat.name}</Table.Cell>;
    }
  };

  const showLoader = isLoading || (isFetching && !boardStats);
  return (
    <div className="leaderboardTable">
      <div className="leaderboardHeader">
        <Header as="h4">
          {title}
          {isFetching && <Loader active inline size="mini" style={{ marginLeft: "10px" }} />}
        </Header>
        <div className={"headerActions"}>
          <Dropdown
            onChange={(_, data) => handleTimeChange(data.value as string)}
            options={timeOptions.map(t => ({ key: t.label, text: t.label, value: t.label }))}
            value={startTime?.label}
            className={"timeDropdown"}
            inline
          />
          {paginated && (
            <Button
              className={"exportButton"}
              onClick={() => exportStats()}
              loading={exporting}
              floated={"right"}
              size={"tiny"}
              secondary
            >
              Export
            </Button>
          )}
        </div>
      </div>

      <Table compact="very" striped>
        <Table.Header>
          <Table.Row>
            <Table.HeaderCell>Rank</Table.HeaderCell>
            <Table.HeaderCell>
              {metricOptions.length > 1 && (
                <Dropdown
                  onChange={(_, data) => handleMetricChange(data.value as LeaderboardMetric)}
                  options={metricOptions}
                  value={metric.value}
                  className={"metricDropdown"}
                  inline
                />
              )}
              {metricOptions.length == 1 && metric.text}
            </Table.HeaderCell>
            {statHeaders}
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {!showLoader && boardStats?.results?.length == 0 && (
            <Table.Row>
              <Table.Cell colSpan={3} textAlign={"center"}>
                No data available for this time period.
              </Table.Cell>
            </Table.Row>
          )}
          {boardStats?.results?.map((stat, index) => {
            return (
              <Table.Row key={`leaderboard-stat-${index}`}>
                <Table.Cell width={1} textAlign={"center"}>
                  {trophy(index + 1 + (page - 1) * effectivePerPage)}
                </Table.Cell>
                <Table.Cell width={1} textAlign={"center"}>
                  <span className={"statValue"}>
                    {statPrefix(stat)}
                    {safeFormatNumber(stat.score)}
                    {statSuffix(stat)}
                  </span>
                </Table.Cell>
                {renderCells(stat)}
              </Table.Row>
            );
          })}
        </Table.Body>
      </Table>
      {paginated && (
        <div className={"paginationContainer"}>
          <Pagination
            activePage={page}
            onPageChange={(_, { activePage }) => setPage(activePage as number)}
            totalPages={totalPages}
            disabled={isFetching}
            pointing
            secondary
          />
        </div>
      )}
      {onViewAll && (
        <a className={"viewAllLink"} onClick={() => onViewAll && onViewAll(startTime.key, metric.value)}>
          View All{" "}
          {boardStats?.pagination?.totalCount && boardStats?.pagination?.totalCount > effectivePerPage
            ? `(${safeFormatNumber(boardStats.pagination.totalCount)})`
            : ""}
        </a>
      )}
    </div>
  );
};

export const LeaderboardTable = LeaderboardTableFC;
